import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RequestAccessPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-access`, formData);
      setSubmitted(true);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('requestAccessPage.kunneIkkeSendeForesporsel', 'Kunne ikke sende forespørsel'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4 flex gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl">{t('auto.foresporsel_mottatt', 'Request Received!')}</CardTitle>
              <CardDescription className="text-base mt-2">
                {t('requestAccessPage.tilgangsforesporselenDinErSendtTil', 'Tilgangsforespørselen din er sendt til administratoren. Du får en e-post når forespørselen er behandlet.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login">
                <Button className="w-full gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {t('requestAccessPage.tilbakeTilInnlogging', 'Tilbake til innlogging')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4 flex gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{t('requestAccessPage.beOmTilgang', 'Be om Tilgang')}</CardTitle>
            <CardDescription>
              {t('requestAccessPage.fyllUtSkjemaetUnderFor', 'Fyll ut skjemaet under for å be om tilgang som kunde. En administrator behandler forespørselen din.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">{t('requestAccessPage.fulltNavn', 'Fullt Navn *')}</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e: any) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  placeholder={t('auth.fullNamePH')}
                />
              </div>

              <div>
                <Label htmlFor="email">{t('requestAccessPage.ePostadresse', 'E-postadresse *')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder={t('auth.emailPH2')}
                />
              </div>

              <div>
                <Label htmlFor="company_name">{t('requestAccessPage.firmanavn', 'Firmanavn *')}</Label>
                <Input
                  id="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={(e: any) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  placeholder={t('auth.companyNamePH')}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Sender…' : t('auto.send_foresprsel_218', 'Send Forespørsel')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <Link
                to="/login"
                className="text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-slate-200 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('requestAccessPage.tilbakeTilInnlogging', 'Tilbake til innlogging')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
